import React, { useState } from "react";
import { motion } from "framer-motion";
import LegalModal from "../components/LegalModal";

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [modal, setModal] = useState(null); // "privacy" | "terms" | null

  return (
    <footer className="footer">
      <div className="footer-content">
        <motion.div
          className="footer-grid"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {/* Brand column */}
          <div className="footer-col footer-col-brand">
            <h3 className="footer-logo">EmberByte</h3>
            <p className="footer-tagline">One Spark. Infinite Possibilities.</p>
            <p className="footer-desc">
              We build AI-powered apps, websites, mobile apps, cloud solutions, and
              games that solve real business problems and help you grow.
            </p>
          </div>

          {/* Company links */}
          <div className="footer-col">
            <h4 className="footer-col-title">Company</h4>
            <ul className="footer-col-links">
              <li><a href="#about">About Us</a></li>
              <li><a href="#industries">Industries</a></li>
              <li><a href="#process">How We Work</a></li>
              <li><a href="#faq">FAQ</a></li>
            </ul>
          </div>

          {/* Services links */}
          <div className="footer-col">
            <h4 className="footer-col-title">What We Do</h4>
            <ul className="footer-col-links">
              <li><a href="#services">Services</a></li>
              <li><a href="#projects">Solutions</a></li>
              <li><a href="#games">Game Development</a></li>
              <li><a href="#technologies">Tech Stack</a></li>
            </ul>
          </div>

          {/* CTA column */}
          <div className="footer-col">
            <h4 className="footer-col-title">Get In Touch</h4>
            <p className="footer-desc">
              Have a project or an idea? Let's build it together.
            </p>
            <a href="#contact" className="footer-cta-button">
              Start Your Project
            </a>
          </div>
        </motion.div>

        <div className="footer-divider"></div>

        <motion.div
          className="footer-bottom"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <p className="footer-copyright">
            © {currentYear} EmberByte Digital Solutions LLP. All rights reserved.
          </p>

          <div className="footer-legal">
            <button className="footer-legal-link" onClick={() => setModal("privacy")}>
              Privacy Policy
            </button>
            <span className="separator">•</span>
            <button className="footer-legal-link" onClick={() => setModal("terms")}>
              Terms of Service
            </button>
          </div>
        </motion.div>
      </div>

      {/* Subtle glow effect */}
      <div className="footer-ambient-glow"></div>

      <LegalModal
        open={modal === "privacy"}
        onClose={() => setModal(null)}
        title="Privacy Policy"
      >
        <p className="legal-updated">Last updated: {currentYear}</p>

        <p>
          EmberByte Digital Solutions LLP ("we", "us", "our") respects your privacy. This
          policy explains what information we collect through this website and how we use it.
        </p>

        <h4>Information We Collect</h4>
        <p>
          We only collect the information you choose to share through our inquiry form:
          your name, email address, and optionally your company name, budget range, and
          your project message.
        </p>

        <h4>How We Use It</h4>
        <p>
          We use this information solely to respond to your inquiry and discuss your
          project. We do not use it for advertising or unrelated marketing.
        </p>

        <h4>Data Storage</h4>
        <p>
          We do not store your form submissions in any database. When you submit the form,
          the details are sent directly to our business email inbox so we can reply to you.
        </p>

        <h4>Sharing</h4>
        <p>
          We do not sell, rent, or share your personal information with third parties. Your
          details are used only within EmberByte to respond to you.
        </p>

        <h4>Third-Party Services</h4>
        <p>
          Our website uses Google Fonts to display text, which may involve your browser
          requesting font files from Google's servers. We use a standard email service to
          receive inquiry messages. We do not run advertising or third-party tracking on
          this site.
        </p>

        <h4>Your Choices</h4>
        <p>
          If you'd like us to delete an email you've sent us or have any privacy questions,
          simply reach out through our contact form and we'll take care of it.
        </p>

        <h4>Contact</h4>
        <p>
          For any questions about this Privacy Policy, please contact us through the
          inquiry form on this website.
        </p>
      </LegalModal>

      <LegalModal
        open={modal === "terms"}
        onClose={() => setModal(null)}
        title="Terms of Service"
      >
        <p className="legal-updated">Last updated: {currentYear}</p>

        <p>
          By using this website, you agree to the following terms. Please read them
          carefully.
        </p>

        <h4>About This Website</h4>
        <p>
          This website provides information about the services offered by EmberByte Digital
          Solutions LLP. The content is for general informational purposes and may be
          updated at any time.
        </p>

        <h4>Services & Quotes</h4>
        <p>
          Any services, timelines, or pricing discussed on this website are indicative.
          Actual project scope, deliverables, timelines, and costs are defined in a separate
          written agreement or proposal shared with each client before work begins.
        </p>

        <h4>Inquiries</h4>
        <p>
          Submitting an inquiry through our form does not create any contract or obligation.
          It is simply a request to start a conversation about your project.
        </p>

        <h4>Intellectual Property</h4>
        <p>
          The EmberByte name, logo, and website content belong to EmberByte Digital
          Solutions LLP and may not be copied or reused without permission.
        </p>

        <h4>Limitation of Liability</h4>
        <p>
          We aim to keep the information on this website accurate and up to date, but we make
          no guarantees. We are not liable for any decisions made solely based on website
          content.
        </p>

        <h4>Contact</h4>
        <p>
          For any questions about these terms, please contact us through the inquiry form on
          this website.
        </p>
      </LegalModal>
    </footer>
  );
}
